import React, { useState } from 'react';
import styles from './ListView.module.css';
import { PlanningItem, PlanningType } from './types';

interface ListViewProps {
  items: PlanningItem[];
  onEditItem: (item: PlanningItem) => void;
  onDeleteItem: (id: string) => void;
}

export const ListView: React.FC<ListViewProps> = ({ items, onEditItem, onDeleteItem }) => {
  const [filterType, setFilterType] = useState<PlanningType | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredItems = items
    .filter(item => filterType === 'all' || item.type === filterType)
    .filter(item => 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.targetAudience.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.responsible.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const getTypeColor = (type: PlanningItem['type']) => {
    switch (type) {
      case 'mijlpaal':
        return styles.typeMilestone;
      case 'communicatie':
        return styles.typeCommunication;
      case 'bijeenkomst':
        return styles.typeMeeting;
      case 'deadline':
        return styles.typeDeadline;
      default:
        return '';
    }
  };

  const getTypeLabel = (type: PlanningItem['type']) => {
    switch (type) {
      case 'mijlpaal':
        return 'Mijlpaal';
      case 'communicatie':
        return 'Communicatie';
      case 'bijeenkomst':
        return 'Bijeenkomst';
      case 'deadline':
        return 'Deadline';
      default:
        return type;
    }
  };

  return (
    <div className={styles.listView}>
      <div className={styles.listControls}>
        <div className={styles.searchBox}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Zoeken..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterChips}>
          <button
            className={`${styles.filterChip} ${filterType === 'all' ? styles.active : ''}`}
            onClick={() => setFilterType('all')}
          >
            Alles ({items.length})
          </button>
          <button
            className={`${styles.filterChip} ${filterType === 'mijlpaal' ? styles.active : ''}`}
            onClick={() => setFilterType('mijlpaal')}
          >
            Mijlpalen ({items.filter(i => i.type === 'mijlpaal').length})
          </button>
          <button
            className={`${styles.filterChip} ${filterType === 'communicatie' ? styles.active : ''}`}
            onClick={() => setFilterType('communicatie')}
          >
            Communicatie ({items.filter(i => i.type === 'communicatie').length})
          </button>
          <button
            className={`${styles.filterChip} ${filterType === 'bijeenkomst' ? styles.active : ''}`}
            onClick={() => setFilterType('bijeenkomst')}
          >
            Bijeenkomsten ({items.filter(i => i.type === 'bijeenkomst').length})
          </button>
          <button
            className={`${styles.filterChip} ${filterType === 'deadline' ? styles.active : ''}`}
            onClick={() => setFilterType('deadline')}
          >
            Deadlines ({items.filter(i => i.type === 'deadline').length})
          </button>
        </div>
      </div>

      <div className={styles.listContainer}>
        {filteredItems.length === 0 ? (
          <div className={styles.emptyState}>
            <p>Geen planning items gevonden</p>
          </div>
        ) : (
          <div className={styles.listItems}>
            {filteredItems.map(item => {
              const date = new Date(item.date);
              
              return (
                <div key={item.id} className={styles.listItem}>
                  <div className={styles.dateColumn}>
                    <div className={styles.dateDay}>{date.getDate()}</div>
                    <div className={styles.dateMonth}>
                      {date.toLocaleDateString('nl-NL', { month: 'short' })}
                    </div>
                    <div className={styles.dateYear}>{date.getFullYear()}</div>
                  </div>

                  <div className={styles.itemContent}>
                    <div className={styles.itemHeader}>
                      <span className={`${styles.typeChip} ${getTypeColor(item.type)}`}>
                        {getTypeLabel(item.type)}
                      </span>
                      {item.werkCategorie === 'raakvlak' && (
                        <span className={styles.raakvlakBadge}>Raakvlak</span>
                      )}
                      <span className={`${styles.statusChip} ${styles[`status${item.status.replace(/\s/g, '')}`]}`}>
                        {item.status}
                      </span>
                    </div>

                    <h3 className={styles.itemTitle}>{item.title}</h3>

                    <div className={styles.itemMeta}>
                      <div className={styles.metaItem}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        {item.time}
                      </div>

                      <div className={styles.metaItem}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        {item.targetAudience}
                      </div>

                      {item.channel && (
                        <div className={styles.metaItem}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                            <polyline points="22,6 12,13 2,6" />
                          </svg>
                          {item.channel}
                        </div>
                      )}

                      <div className={styles.metaItem}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        {item.responsible}
                      </div>

                      {item.location && (
                        <div className={styles.metaItem}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          {item.location}
                        </div>
                      )}
                    </div>

                    {item.dependencies && item.dependencies.length > 0 && (
                      <div className={styles.dependencies}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                        </svg>
                        Afhankelijk van {item.dependencies.length} andere item(s)
                      </div>
                    )}
                  </div>

                  <div className={styles.itemActions}>
                    <button
                      className={styles.actionButton}
                      onClick={() => onEditItem(item)}
                      title="Bewerken"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      className={styles.actionButton}
                      onClick={() => onDeleteItem(item.id)}
                      title="Verwijderen"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};